import type { MouseEventHandler } from 'react'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import styled from 'styled-components'
import { rem } from 'polished'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import OfferBadge from 'components/Common/OfferBadge'
import { isBedbankOffer, isCruiseOffer, isFlashHotelOffer, isLEHotel, isLEOffer, isTourV2Offer, isVillaOffer } from 'lib/offer/offerTypes'
import { mediaQueryUp } from 'components/utils/breakpoint'
import variables from 'styles/vendor/bootstrap/variables'
import type { ImageCarouselInterstitial, ImageCarouselRefInterface } from 'LuxKit/Carousel/BaseImageCarousel'
import type { VimeoPlayerOptions } from 'components/Common/Video/VimeoVideo'
import VimeoVideo from 'components/Common/Video/VimeoVideo'
import Clickable from 'components/Common/Clickable'
import ModalContext from 'contexts/ModalContext'
import VideoLightbox from 'LuxKit/Carousel/VideoLightbox'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import ImageGalleryLightbox from 'LuxKit/Carousel/ImageGalleryLightbox'
import ImageGalleryLightboxWithThumbnails from 'LuxKit/Carousel/ImageGalleryLightboxWithThumbnails'
import TextButton from 'LuxKit/Button/TextButton'
import SolidPlayIcon from 'LuxKit/Icons/solid/SolidPlayIcon'
import LineImagesIcon from 'LuxKit/Icons/line/LineImagesIcon'
import { EmptyArray, take } from 'lib/array/arrayUtils'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Image from 'components/Common/Image'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton, { ExperienceBookmarkButton, TourBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import ShareModal from 'components/Common/Modals/ShareModal'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import type { ShareConfig } from 'components/Common/Modals/ShareModal/ShareModal'
import TourProductTypeLabel from 'components/Tours/TourProductTypeLabel'
import { useInView } from 'react-intersection-observer'
import MasterModalContext from 'contexts/MasterModalContext'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import SolidStarCircleIcon from 'LuxKit/Icons/solid/SolidStarCircleIcon'
import { isTourLimitedTimeOffer } from 'lib/tours/tourUtils'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import { carouselImageImpressionEvent } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import { v4 as uuidv4 } from 'uuid'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import { getOptimizelyEnabledDecision } from 'lib/optimizely/optimizelyUtils'
import NestableClickable from 'components/Common/NestableClickable'
import { useAppSelector } from 'hooks/reduxHooks'
import { isPaidSessionSelector } from 'selectors/marketingSelectors'
import { useIntl } from 'react-intl'
import OfferGallerySectionedLightbox from './OfferGallerySectionedLightbox'

export const OFFER_TOP_IMAGE_PERF_ATTR = { 'data-perf': 'offer-top-image' }

const VimeoWrapper = styled(Clickable)`
  height: 100%;
  width: 100%;
  position: relative;
  background-color: ${props => props.theme.palette.neutral.default.one};

  > * {
    pointer-events: none;
  }
`

const VimeoWrapperBackground = styled.div`
  height: 100%;
  width: 100%;
  background-color: ${props => props.theme.palette.neutral.default.one};
`

const MobileHuggingContainer = styled(LayoutContainer)`
  padding: 0;

  ${mediaQueryUp.tablet} {
    padding-right: ${rem(variables.gutterWidth / 2)};
    padding-left: ${rem(variables.gutterWidth / 2)};
  }
`

const CarouselClickable = styled(NestableClickable)`
  position: relative;
  aspect-ratio: var(--gallery-xs-aspect);

  ${mediaQueryUp.tablet} {
    aspect-ratio: var(--gallery-sm-aspect, var(--gallery-xs-aspect));
  }

  ${mediaQueryUp.desktop} {
    aspect-ratio: var(--gallery-md-aspect, var(--gallery-sm-aspect, var(--gallery-xs-aspect)));
  }

  ${mediaQueryUp.largeDesktop} {
    aspect-ratio: var(--gallery-lg-aspect, var(--gallery-md-aspect, var(--gallery-sm-aspect, var(--gallery-xs-aspect))));
  }
`

type CarouselAspectStyle = React.CSSProperties & {
  '--gallery-xs-aspect'?: string
  '--gallery-sm-aspect'?: string
  '--gallery-md-aspect'?: string
  '--gallery-lg-aspect'?: string
}

function toCssAspect(aspect?: string): string | undefined {
  return aspect?.replace(':', ' / ')
}

const OfferOverlayContainer = styled(LabelGroup)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const FloatingOfferBadge = styled(OfferBadge)`
  margin: ${rem(16)};
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 50% 50%;
  gap: ${rem(8)};
  aspect-ratio: 2.78;
  position: relative;

  > :first-child,
  > ${FloatingOfferBadge} {
    grid-column: 1 / 2;
    grid-row: 1 / 3;
  }

  > ${FloatingOfferBadge} {
    align-self: start;
    justify-self: end;
  }
`

const VARIANT_THUMBNAIL_HEIGHT = 150

const VariantBottomRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${rem(8)};
  height: ${rem(VARIANT_THUMBNAIL_HEIGHT)};
  overflow: hidden;

  > * {
    height: ${rem(VARIANT_THUMBNAIL_HEIGHT)};
    overflow: hidden;
  }
`

const VariantBottomRowSection = styled.div`
  display: grid;
  grid-template-columns: repeat(var(--cols), 1fr);
  gap: ${rem(8)};
  height: ${rem(VARIANT_THUMBNAIL_HEIGHT)};

  > * {
    height: ${rem(VARIANT_THUMBNAIL_HEIGHT)};
    width: 100%;
    overflow: hidden;

    img, picture {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`

const VariantGridContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  width: 100%;

  > ${GridContainer} {
    width: 100%;
    flex-shrink: 0;
    overflow: hidden;
  }

  > ${VariantBottomRow} {
    width: 100%;
    flex-shrink: 0;
    position: relative;
    z-index: 1;
  }
`

const GalleryButtons = styled(Group)`
  display: flex;
  pointer-events: auto;
  gap: ${rem(8)};
  justify-content: flex-end;
  position: absolute;
  bottom: ${rem(16)};
  right: ${rem(16)};
`

const OverlayLabelContainer = styled(LabelGroup)`
  position: absolute;
  top: ${rem(16)};
  left: ${rem(16)};
`

const TopRightActionContainer = styled(Group)`
  position: absolute;
  pointer-events: auto;
  top: ${rem(12)};
  right: ${rem(12)};
`

const StyledLayoutContainer = styled(LayoutContainer)`
  padding-top: 0;
  ${mediaQueryUp.tablet}{
    padding-top: ${rem(16)};
  }
`

const defaultImageProps: ImageParams = { tabletWidth: '400px', quality: 'good' }
const mainImageProps: ImageParams = { tabletWidth: '800px', quality: 'best' }
const carouselImageParams: ImageParams = { mobileAspectRatio: '16:9', tabletAspectRatio: '16:9', quality: 'good' }
const carouselAspectStyle: CarouselAspectStyle = {
  '--gallery-xs-aspect': toCssAspect(carouselImageParams.mobileAspectRatio),
  '--gallery-sm-aspect': toCssAspect(carouselImageParams.tabletAspectRatio),
  '--gallery-md-aspect': toCssAspect(carouselImageParams.desktopAspectRatio),
  '--gallery-lg-aspect': toCssAspect(carouselImageParams.largeDesktopAspectRatio),
}
const loopQuery: VimeoPlayerOptions = { loop: true, autoplay: true, controls: true }
const mutedLoopQuery: VimeoPlayerOptions = { muted: true, loop: true, controls: true }

// Paid gallery variant constants
const VARIANT_MIN_IMAGES = 8
const VARIANT_TOP_IMAGES_COUNT = 3
const VARIANT_BOTTOM_LEFT_COUNT = 3
const VARIANT_BOTTOM_RIGHT_COUNT = 2
const VARIANT_BOTTOM_LEFT_STYLE = { '--cols': VARIANT_BOTTOM_LEFT_COUNT } as React.CSSProperties
const VARIANT_BOTTOM_RIGHT_STYLE = { '--cols': VARIANT_BOTTOM_RIGHT_COUNT } as React.CSSProperties

interface GalleryView {
  images: Array<App.Image>
  videoIds: Array<string>
  productType?: App.ProductType
  additionalLabels?: React.ReactNode
  badge?: App.OfferBadge
}

interface Props {
  offer?: App.AnyOffer
  tourVariation?: App.Tours.TourV2OfferVariation
  experience?: App.ExperienceOffer
  shareModalConfig?: ShareConfig
  /**
   * Will set the video as the first slide and
   * auto-play it if there is one on the offer
   *
   * NOTE: Currently disabled
   */
  autoPlayVideo?: boolean
  /**
   * Called when the top hero image (first image) has finished loading in grid mode
   */
  onTopHeroImageLoad?: () => void
  /**
   * Charter bundle route identifier for conditional rendering
   */
  charterBundleRoute?: App.CharterBundleRoute
}

/**
 * An offer page specific gallery that will show the images/videos for the given
 * offer types supported
 */
function OfferPageGallery(props: Props) {
  const intl = useIntl()
  const { offer, experience, tourVariation, shareModalConfig, onTopHeroImageLoad, charterBundleRoute } = props
  const [hasAutoPlayed, setHasAutoPlayed] = useState(true)
  const [hasAutoPaused, setHasAutoPaused] = useState(false)
  const isMobileDevice = useIsMobileDevice()
  const showLightbox = useContext(ModalContext)
  const modalState = useContext(MasterModalContext)
  const videoRef = useRef<any>(null)
  const carouselRef = useRef<ImageCarouselRefInterface | null>(null)
  const [ref, inView] = useInView()
  const autoPlayVideo = isTourV2Offer(offer)
  const listId = useMemo(() => uuidv4(), [])

  const isDesktop = useScreenSizeUp('desktop')
  const isPaidSession = useAppSelector(isPaidSessionSelector)
  const isEligibleHotelOffer = useMemo(
    () => (isLEHotel(offer) || isBedbankOffer(offer)) && !isFlashHotelOffer(offer),
    [offer],
  )
  const isEligibleAccommodationOffer = useMemo(
    () => (isLEHotel(offer) || isBedbankOffer(offer)) && !isVillaOffer(offer),
    [offer],
  )
  const shouldUseThumbnailLightbox = isDesktop
  // If this wins, test other product types (flash, tours, cruises, etc.) on paid traffic.
  const paidGalleryVariantExperiment = useOptimizelyExperiment(
    OptimizelyExperiments.growthPaidOfferGalleryVariant,
    isDesktop && isPaidSession && isEligibleHotelOffer,
  )
  const shouldShowPaidGalleryVariant = getOptimizelyEnabledDecision(paidGalleryVariantExperiment)
  const sectionedGalleryExperiment = useOptimizelyExperiment(
    OptimizelyExperiments.croOfferGalleryGrid,
    isDesktop && isEligibleAccommodationOffer,
  )
  const shouldShowSectionedGalleryVariant = getOptimizelyEnabledDecision(sectionedGalleryExperiment)
  const shouldUseSectionedGalleryLightbox = isDesktop && shouldShowSectionedGalleryVariant

  const {
    images,
    videoIds,
    badge,
    productType,
    additionalLabels,
  } = useMemo((): GalleryView => {
    if (isTourV2Offer(offer)) {
      return {
        videoIds: offer.video ? [offer.video.id] : [],
        images: tourVariation?.images ?? [],
        additionalLabels: <LabelGroup>
          {isTourLimitedTimeOffer(offer) &&
            <StructureLabel startIcon={<SolidStarCircleIcon />} variant="exclusive">
              {intl.formatMessage({
                id: 'K1tIBN7w1L',
                defaultMessage: 'Limited Time Lux Exclusive',
                description: 'Label for limited time exclusive tour offers',
              })}
            </StructureLabel>}
          <TourProductTypeLabel productType={offer.productType} />
        </LabelGroup>,
      }
    } else if (experience) {
      return {
        videoIds: experience.video ? [experience.video] : [],
        images: experience.images,
        productType: undefined,
        badge: undefined,
      }
    } else if (isLEOffer(offer)) {
      let productType: App.ProductType | undefined = offer.productType
      if (isLPCTacticalEnabled() && offer.hasTactical) {
        productType = 'limited_time_special'
      } else if (offer.isPartnerProperty) {
        productType = undefined
      }

      return {
        videoIds: offer.vimeoVideoId ? [offer.vimeoVideoId] : [],
        badge: offer.badge,
        images: offer.images,
        productType: charterBundleRoute ? undefined : productType,
        additionalLabels: <>
          {charterBundleRoute && <StructureLabel variant="maldives-charter">
            {intl.formatMessage({
              id: 'b7KywUWBoZ',
              defaultMessage: 'Hotel & Flight bundle',
              description: 'Label for hotel and flight bundle offers',
            })}
          </StructureLabel>}
          {offer.availabilitySchedule?.end && <OfferUrgencyCountdownLabel endDate={offer.availabilitySchedule.end} tagPlace="offer-gallery" />}
          <OfferPartnershipLabel offer={offer} />
        </>,
      }
    } else if (isBedbankOffer(offer)) {
      return {
        videoIds: offer.videos?.filter(vid => vid.type === 'vimeo').map(vid => vid.id) ?? [],
        badge: undefined,
        images: offer.images,
        productType: offer.promotions.length > 0 ? undefined : offer.productType,
      }
    } else if (isCruiseOffer(offer)) {
      return {
        images: offer.images,
        productType: offer.productType,
        badge: undefined,
        videoIds: [],
      }
    }
    return {
      images: [],
      videoIds: [],
    }
  }, [experience, offer, tourVariation, charterBundleRoute, intl])

  const shouldUseGalleryVariant = useMemo(
    () => shouldShowPaidGalleryVariant && images.length >= VARIANT_MIN_IMAGES,
    [images.length, shouldShowPaidGalleryVariant],
  )

  const videoOnPause = useCallback(() => {
    if (!hasAutoPaused) {
      setHasAutoPlayed(false)
    }
    setHasAutoPaused(false)
  }, [hasAutoPaused, setHasAutoPlayed, setHasAutoPaused])

  const carouselInterstitials: Array<ImageCarouselInterstitial> | undefined = useMemo(() => {
    if (videoIds.length > 0) {
      const position = (shouldUseSectionedGalleryLightbox || autoPlayVideo) ? 1 : 2
      return videoIds.map((videoId, index) => ({
        id: `video-${videoId}`,
        position: position + index,
        element: (<>
          {!autoPlayVideo && <VimeoWrapper onClick={() => showLightbox(<VideoLightbox videoId={videoId} playOnMount query={loopQuery} />)}>
            <VimeoVideo vimeoVideoId={videoId} query={mutedLoopQuery} />
          </VimeoWrapper>}
          {autoPlayVideo && <VimeoWrapperBackground ref={ref}>
            <VimeoVideo vimeoVideoId={videoId} ref={videoRef} onPaused={videoOnPause} query={mutedLoopQuery} />
          </VimeoWrapperBackground>}
        </>),
      }))
    }

    return undefined
  }, [videoIds, shouldUseSectionedGalleryLightbox, autoPlayVideo, ref, videoOnPause, showLightbox])

  const gridInterstitials: Array<ImageCarouselInterstitial> | undefined = useMemo(() => {
    if (!autoPlayVideo && videoIds.length > 0) {
      return videoIds.map((videoId, index) => ({
        id: `video-${videoId}`,
        position: (shouldUseSectionedGalleryLightbox ? 1 : 2) + index,
        element: <VimeoVideo vimeoVideoId={videoId} query={mutedLoopQuery} />,
      }))
    }

    return undefined
  }, [autoPlayVideo, shouldUseSectionedGalleryLightbox, videoIds])

  const showVideoLightbox = useCallback(() => {
    if (videoIds.length > 0) {
      showLightbox(<VideoLightbox videoId={videoIds[0]} playOnMount query={loopQuery} />)
    }
  }, [videoIds, showLightbox])

  const [galleryMode, galleryImages] = useMemo<[] | ['grid', Array<App.Image>] | ['carousel', Array<App.Image>]>(() => {
    if (!images.length) return []

    if (!isMobileDevice && images.length >= 3) {
      if (shouldUseGalleryVariant) {
        return ['grid', take(images, VARIANT_MIN_IMAGES)]
      }

      const imageToShow = autoPlayVideo && !!videoIds.length ? 2 : 3
      return ['grid', take(images, imageToShow)]
    }

    return ['carousel', images]
  }, [images, isMobileDevice, autoPlayVideo, shouldUseGalleryVariant, videoIds.length],
  )

  const variantImages = useMemo(() => {
    if (!shouldUseGalleryVariant) return []
    return take(images, VARIANT_MIN_IMAGES)
  }, [images, shouldUseGalleryVariant])

  const variantTopImages = useMemo(() => variantImages.slice(0, VARIANT_TOP_IMAGES_COUNT), [variantImages])
  const variantBottomImages = useMemo(() => variantImages.slice(VARIANT_TOP_IMAGES_COUNT, VARIANT_MIN_IMAGES), [variantImages])

  useEffect(() => {
    if (autoPlayVideo && hasAutoPlayed) {
      if (inView && !modalState.open) {
        videoRef.current?.play('carousel')
      } else {
        setHasAutoPaused(true)
        videoRef.current?.pause()
      }
    }
  }, [inView, offer, hasAutoPlayed, modalState, autoPlayVideo])

  const handleImageChange = useCallback((index: number, image?: App.Image) => {
    if (image && offer) {
      Analytics.trackEvent(
        carouselImageImpressionEvent(
          image,
          index + 1,
          'offer-page-gallery',
          listId,
          offer,
        ),
      )
    }
  }, [offer, listId])

  const getGridImageIndex = useCallback((imageIndex: number) => {
    if (videoIds.length && !autoPlayVideo) {
      const shouldOffsetAllImages = shouldUseSectionedGalleryLightbox
      if (shouldOffsetAllImages || imageIndex !== 0) {
        return imageIndex + 1
      }
    }
    return imageIndex
  }, [autoPlayVideo, shouldUseSectionedGalleryLightbox, videoIds.length])

  const openGalleryLightboxAtIndex = useCallback((index: number, initialView: 'grid' | 'single' = 'single') => {
    if (shouldUseSectionedGalleryLightbox) {
      showLightbox(<OfferGallerySectionedLightbox
        offer={offer}
        images={images}
        interstitials={gridInterstitials ?? EmptyArray}
        defaultIndex={index}
        initialView={initialView}
        onImageChange={(nextIndex: number, image?: App.Image) => {
          handleImageChange(nextIndex, image)
          carouselRef.current?.goToIndex(nextIndex, 'instant')
        }}
      />)
      return
    }
    const interstitials = galleryMode === 'grid' ? gridInterstitials : carouselInterstitials
    const lightboxProps = {
      images,
      defaultIndex: index,
      interstitials,
      onImageChange: (nextIndex: number, image?: App.Image) => {
        handleImageChange(nextIndex, image)
        carouselRef.current?.goToIndex(nextIndex, 'instant')
      },
    }

    if (shouldUseThumbnailLightbox) {
      showLightbox(<ImageGalleryLightboxWithThumbnails {...lightboxProps} />)
      return
    }

    // Control: Original lightbox
    showLightbox(<ImageGalleryLightbox {...lightboxProps} />)
  }, [
    carouselInterstitials,
    galleryMode,
    gridInterstitials,
    handleImageChange,
    images,
    offer,
    shouldUseSectionedGalleryLightbox,
    showLightbox,
    shouldUseThumbnailLightbox,
  ])

  const handleImageClick = useCallback<MouseEventHandler<HTMLPictureElement>>((event) => {
    const imageIndex = Number(event.currentTarget.dataset.index) || 0
    openGalleryLightboxAtIndex(imageIndex, 'grid')
  }, [openGalleryLightboxAtIndex])

  const handleOpenGalleryGrid = useCallback(() => {
    openGalleryLightboxAtIndex(0, 'grid')
  }, [openGalleryLightboxAtIndex])

  const handleCarouselOpen = useCallback(() => {
    const index = carouselRef.current?.getCurrentImageIndex() ?? 0
    openGalleryLightboxAtIndex(index)
  }, [openGalleryLightboxAtIndex])

  const shouldShowToolTip = !useScreenSizeUp('tablet')

  const gridOverlayLabels = useMemo(() => (
    <OverlayLabelContainer>
      {productType && <ProductTypeLabel productType={productType} />}
      {additionalLabels}
    </OverlayLabelContainer>
  ), [productType, additionalLabels])

  const gridButtons = useMemo(() => (
    <GalleryButtons direction="horizontal">
      {!autoPlayVideo && videoIds.length > 0 &&
        <TextButton
          kind="secondary"
          variant="dark"
          startIcon={<SolidPlayIcon />}
          onClick={showVideoLightbox}
        >
          {intl.formatMessage({
            id: '23GYdnBZ1Q',
            defaultMessage: 'Play video',
            description: 'Button text to play video in gallery',
          })}
        </TextButton>}
      <CSSBreakpoint min="desktop">
        {images.length > 1 &&
          <TextButton
            kind="secondary"
            variant="dark"
            startIcon={<LineImagesIcon />}
            onClick={handleOpenGalleryGrid}
          >
            {intl.formatMessage({
              id: '9Jie2qAaem',
              defaultMessage: 'View gallery',
              description: 'Button text to view gallery',
            })}
          </TextButton>}
      </CSSBreakpoint>
    </GalleryButtons>
  ), [autoPlayVideo, handleOpenGalleryGrid, images.length, showVideoLightbox, videoIds.length, intl])

  const gridBadge = useMemo(() => (
    badge ? <FloatingOfferBadge badge={badge} /> : null
  ), [badge])

  return <Pane>
    <StyledLayoutContainer>
      {galleryMode === 'grid' && !shouldUseGalleryVariant &&
        <GridContainer>
          {autoPlayVideo && !!videoIds.length && <VimeoWrapperBackground ref={ref}>
            <VimeoVideo vimeoVideoId={videoIds[0]} ref={videoRef} onPaused={videoOnPause} query={mutedLoopQuery} />
          </VimeoWrapperBackground>}
          {galleryImages.map((image, imageIndex) =>
            <Clickable
              key={image.id ?? image.url}
              onClick={handleImageClick}
              data-index={getGridImageIndex(imageIndex)}
            >
              {image.id && <ResponsiveImage
                id={image.id}
                fit="center"
                {...(imageIndex === 0 ? OFFER_TOP_IMAGE_PERF_ATTR : {})}
                {...(imageIndex === 0 ? mainImageProps : defaultImageProps)}
                {...(imageIndex === 0 ? { onLoad: onTopHeroImageLoad } : {})}
              />}
              {image.url && <Image
                image={image}
                fit="center"
                {...(imageIndex === 0 ? OFFER_TOP_IMAGE_PERF_ATTR : {})}
                {...(imageIndex === 0 ? { onLoad: onTopHeroImageLoad } : {})}
              />}
            </Clickable>)}
          {gridOverlayLabels}
          {gridButtons}
          {gridBadge}
        </GridContainer>}
      {galleryMode === 'grid' && shouldUseGalleryVariant &&
        <VariantGridContainer>
          <GridContainer>
            {autoPlayVideo && !!videoIds.length && <VimeoWrapperBackground ref={ref}>
              <VimeoVideo vimeoVideoId={videoIds[0]} ref={videoRef} onPaused={videoOnPause} query={mutedLoopQuery} />
            </VimeoWrapperBackground>}
            {variantTopImages.map((image, imageIndex) =>
              <Clickable
                key={image.id ?? image.url}
                onClick={handleImageClick}
                data-index={getGridImageIndex(imageIndex)}
              >
                {image.id && <ResponsiveImage
                  id={image.id}
                  fit="center"
                  {...(imageIndex === 0 ? OFFER_TOP_IMAGE_PERF_ATTR : {})}
                  {...(imageIndex === 0 ? mainImageProps : defaultImageProps)}
                  {...(imageIndex === 0 ? { onLoad: onTopHeroImageLoad } : {})}
                />}
                {image.url && <Image
                  image={image}
                  fit="center"
                  {...(imageIndex === 0 ? OFFER_TOP_IMAGE_PERF_ATTR : {})}
                  {...(imageIndex === 0 ? { onLoad: onTopHeroImageLoad } : {})}
                />}
              </Clickable>)}
            {gridOverlayLabels}
            {gridButtons}
            {gridBadge}
          </GridContainer>
          <VariantBottomRow>
            <VariantBottomRowSection style={VARIANT_BOTTOM_LEFT_STYLE}>
              {variantBottomImages.slice(0, VARIANT_BOTTOM_LEFT_COUNT).map((image, imageIndex) => {
                const bottomIndex = imageIndex + VARIANT_TOP_IMAGES_COUNT
                return (
                  <Clickable
                    key={image.id ?? image.url}
                    onClick={handleImageClick}
                    data-index={getGridImageIndex(bottomIndex)}
                  >
                    {image.id && <ResponsiveImage
                      id={image.id}
                      fit="center"
                    />}
                    {image.url && <Image
                      image={image}
                      fit="center"
                    />}
                  </Clickable>
                )
              })}
            </VariantBottomRowSection>
            <VariantBottomRowSection style={VARIANT_BOTTOM_RIGHT_STYLE}>
              {variantBottomImages.slice(VARIANT_BOTTOM_LEFT_COUNT).map((image, imageIndex) => {
                const bottomIndex = imageIndex + VARIANT_TOP_IMAGES_COUNT + VARIANT_BOTTOM_LEFT_COUNT
                return (
                  <Clickable
                    key={image.id ?? image.url}
                    onClick={handleImageClick}
                    data-index={getGridImageIndex(bottomIndex)}
                  >
                    {image.id && <ResponsiveImage
                      id={image.id}
                      fit="center"
                    />}
                    {image.url && <Image
                      image={image}
                      fit="center"
                    />}
                  </Clickable>
                )
              })}
            </VariantBottomRowSection>
          </VariantBottomRow>
        </VariantGridContainer>}
    </StyledLayoutContainer>
    {galleryMode === 'carousel' &&
      <MobileHuggingContainer>
        <CarouselClickable
          onClick={handleCarouselOpen}
          style={carouselAspectStyle}
        >
          <ImageCarousel
            carouselRef={carouselRef}
            imageParams={carouselImageParams}
            topLeft={<OfferOverlayContainer>
              {productType && <ProductTypeLabel productType={productType} />}
              {additionalLabels}
            </OfferOverlayContainer>}
            topRight={<>
              {shareModalConfig && <CSSBreakpoint max="mobile">
                <TopRightActionContainer direction="horizontal" gap={8}>
                  <ShareModal shareConfig={shareModalConfig} openOnHash="#share">
                    <ShareWithFriendsButton iconOnly showToolTip={shouldShowToolTip} tooltipPlacement="bottom-end" />
                  </ShareModal>
                  {offer && (
                    <TripGuard>
                      {isTourV2Offer(offer) && <TourBookmarkButton offer={offer} selectedVariation={tourVariation} withLabel="never" />}
                      {!isTourV2Offer(offer) && <BookmarkButton offer={offer} withLabel="never" />}
                    </TripGuard>
                  )}
                  {experience && (
                    <TripGuard>
                      <ExperienceBookmarkButton offer={experience} withLabel="never" />
                    </TripGuard>
                  )}
                </TopRightActionContainer>
              </CSSBreakpoint>}
              {badge && <CSSBreakpoint min="desktop">
                <OfferBadge badge={badge} />
              </CSSBreakpoint>}
            </>}
            bottomRight={<GalleryButtons direction="horizontal">
              {!autoPlayVideo && videoIds.length > 0 &&
                <TextButton
                  kind="secondary"
                  variant="dark"
                  startIcon={<SolidPlayIcon />}
                  onClick={showVideoLightbox}
                >
                  {intl.formatMessage({
                    id: '23GYdnBZ1Q',
                    defaultMessage: 'Play video',
                    description: 'Button text to play video in gallery',
                  })}
                </TextButton>}
              {images.length > 1 &&
                <TextButton
                  kind="secondary"
                  variant="dark"
                  startIcon={<LineImagesIcon />}
                  onClick={handleCarouselOpen}
                >
                  {intl.formatMessage({
                    id: '9Jie2qAaem',
                    defaultMessage: 'View gallery',
                    description: 'Button text to view gallery',
                  })}
                </TextButton>}
            </GalleryButtons>}
            interstitials={carouselInterstitials}
            images={galleryImages}
            eagerLoadFirstImage
            arrowBehaviour="always-visible"
            onImageChange={handleImageChange}
          />
        </CarouselClickable>
      </MobileHuggingContainer>}
  </Pane>
}

export default OfferPageGallery
